import React, { useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PrivateWorkOrderLock from '../private_work_order_lock/PrivateWorkOrderLock';
import WorkOrderStarIcon from '../StarLabels/WorkOrderStarIcon';
import WorkOrderLabelChipsRow from '../StarLabels/WorkOrderLabelChipsRow';
import StarNotePopover from '../StarLabels/StarNotePopover';
import {
	getWorkOrderLabels,
	getWorkOrderStarNote,
	isWorkOrderStarred,
	isWorkOrderLabelsTrackEnabled,
} from '../../data/workOrderStarLabels';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface Props {
	workOrder: { id: number | string; title?: string };
	title?: string;
	onAddLabelClick: () => void;
	// True while the Add Label modal is saving for this WO — shimmers the chips.
	labelsPending?: boolean;
}

const WorkOrderDetailTitleHeader: React.FC<Props> = ({
	workOrder,
	title,
	onAddLabelClick,
	labelsPending = false,
}) => {
	const starred = isWorkOrderStarred(workOrder);
	const starNote = getWorkOrderStarNote(workOrder);
	const workOrderLabels = getWorkOrderLabels(workOrder);
	// Star + label surfaces in the header are hidden when the flag is off.
	const labelsTrackEnabled = isWorkOrderLabelsTrackEnabled();
	const isPrivate = nullSafeGetOrElse('isPrivate', workOrder, false);
	const hasNote = starred && !!starNote;
	const titleText = title != null ? title : workOrder.title;
	// Starred-with-note: the star+note pill takes its own top line; title and labels flow inline below.
	const starOnTop = hasNote;
	const labelsSignature = workOrderLabels.map((label) => label.id).join(',');

	const containerRef = useRef<HTMLDivElement | null>(null);
	const titleMeasureRef = useRef<HTMLSpanElement | null>(null);
	const starMeasureRef = useRef<HTMLSpanElement | null>(null);
	const labelsMeasureRef = useRef<HTMLSpanElement | null>(null);
	const [labelsBelow, setLabelsBelow] = useState(false);

	useLayoutEffect(() => {
		const containerEl = containerRef.current;
		const titleEl = titleMeasureRef.current;
		const labelsEl = labelsMeasureRef.current;
		if (!containerEl || !titleEl || !labelsEl) return undefined;
		const GAP_PX = 12; // gap between star, title and labels
		const check = () => {
			const available = containerEl.clientWidth;
			const starWidth = starMeasureRef.current ? starMeasureRef.current.offsetWidth + GAP_PX : 0;
			const lockWidth = isPrivate ? 40 : 0;
			const needed = starWidth + titleEl.scrollWidth + lockWidth + GAP_PX + labelsEl.scrollWidth;
			setLabelsBelow(needed > available);
		};
		check();
		const observer = new ResizeObserver(check);
		observer.observe(containerEl);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [titleText, starOnTop, isPrivate, labelsSignature]);

	const starPill = labelsTrackEnabled ? (
		<StarNotePopover
			workOrderId={workOrder.id}
			initialStarred={starred}
			initialNote={starNote || ''}
		>
			<WorkOrderStarIcon size="md" withDropdown onDark />
		</StarNotePopover>
	) : null;

	const privateLock = (
		<PrivateWorkOrderLock
			workOrder={workOrder}
			style={{ marginTop: -4, marginRight: 8, marginLeft: 8 }}
			fontSize={24}
		/>
	);

	const labelChips = labelsTrackEnabled ? (
		<WorkOrderLabelChipsRow
			labels={workOrderLabels}
			maxVisible={workOrderLabels.length}
			showAddButton
			addButtonVariant="hollowOnDark"
			onAddClick={onAddLabelClick}
			loading={labelsPending}
			layout="inline"
		/>
	) : null;

	const lock = isPrivate ? (
		<span className="ml-2 inline-flex items-center align-middle">{privateLock}</span>
	) : null;

	const labelsRow = (
		<span ref={labelsMeasureRef} className="inline-flex flex-wrap items-center gap-3 mb-1">
			{labelChips}
		</span>
	);

	const titleAndLabels = labelsBelow ? (
		<div className="flex w-full min-w-0 flex-col gap-3">
			<div className="break-words text-white/90 h2regular">
				{!starOnTop ? (
					<span ref={starMeasureRef} className="mr-3 inline-flex items-center align-middle">
						{starPill}
					</span>
				) : null}
				{titleText}
				{lock}
			</div>
			<div className="flex flex-wrap items-center gap-3 mb-1">{labelsRow}</div>
		</div>
	) : (
		<div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-3">
			{!starOnTop ? (
				<span ref={starMeasureRef} className="inline-flex shrink-0 items-center">
					{starPill}
				</span>
			) : null}
			<span className="break-words text-white/90">
				{titleText}
				{lock}
			</span>
			{labelsRow}
		</div>
	);

	const titleMeasure = (
		<span
			ref={titleMeasureRef}
			aria-hidden="true"
			className="pointer-events-none invisible absolute left-0 top-0 whitespace-nowrap"
		>
			{titleText}
		</span>
	);

	if (starOnTop) {
		return (
			<div
				ref={containerRef}
				className="work-order-detail-title-header relative flex w-full min-w-0 flex-col gap-3 h2regular"
			>
				{titleMeasure}
				<div className="inline-flex items-center">{starPill}</div>
				{titleAndLabels}
			</div>
		);
	}

	return (
		<div ref={containerRef} className="work-order-detail-title-header relative w-full min-w-0">
			{titleMeasure}
			{titleAndLabels}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	labelsPending: nullSafeGetOrElse(
		`work_order_star_labels.labelsPendingById.${nullSafeGet('workOrder.id', ownProps)}`,
		state,
		false
	),
});

export default connect(mapStateToProps)(WorkOrderDetailTitleHeader);
